import { setAppString } from "../constants/AppStrings";
import type { InterfaceAppString } from "../constants/appStringTypes";

/**
 * TCM V2 stores strings per language and namespace under
 * `common_assets/strings/<lng>/<namespace>.json`.
 * The language folders match i18next `supportedLngs`: en, pt-BR, es, de.
 */
const STRINGS_BASE_PATH = "/common_assets/strings";

/**
 * FE string namespaces, fetched and merged in list order (later entries win).
 * Mirrors the product sections in AppStrings.ts; missing secondary bundles are
 * skipped so a partial TCM rollout cannot take the app down.
 */
export const APP_STRING_NAMESPACES: readonly string[] = [
  "pam-pam",
  // Product `pam` CDN file (not `pam-pam`) — hosts Marketing/bonus RESULT NL
  // labels such as PAM_DYNAMIC_BONUS_RESULT_CASH. Merged after pam-pam so
  // these keys win when both files exist.
  "pam",
  // TCM product "Marketing and Communications" for PAM Frontend publishes as
  // `marketing_and_communication-pam.json` (BONENG-881 condition/trigger/
  // segment keys). Missing in some locales (e.g. en) is skipped safely.
  "marketing_and_communication-pam",
  "casino-pam",
  "sportsbook-pam",
  "gamepass-pam",
  "payments-pam",
  "peer_to_peer-pam",
  "pikkem-pam",
  "poker-pam",
  "predictions-pam",
  "account-pam",
  "kyc-pam",
  "wallet-pam",
];

/**
 * The one namespace that still has a TCM V1 fallback (see below). Secondary
 * product namespaces are V2-only: if one is missing it is skipped with a warning.
 */
const LEGACY_FALLBACK_NAMESPACE = "pam-pam";

/**
 * TODO(TCM V1 deprecation): Legacy single-file strings endpoint. Used only as a
 * fallback while some environments still serve TCM V1. Remove this path and the
 * fallback in `loadAppStringsForLanguage` once TCM V1 is deprecated / every
 * environment serves the per-language V2 files.
 */
const LEGACY_STRINGS_PATH = "/pam/strings.json";

/** Tracks the last language+namespaces successfully loaded to avoid redundant re-fetches. */
let loadedKey: string | null = null;

/**
 * Deduplicates concurrent calls for the same language+namespaces: callers that
 * arrive while a fetch is in flight share its promise instead of racing a
 * second fetch whose `setAppString` write could win arbitrarily.
 */
let inFlight: {
  key: string;
  promise: Promise<InterfaceAppString | undefined>;
} | null = null;

function getCloudFrontBaseUrl(): string {
  return process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL ?? "";
}

/** Builds the TCM V2 strings URL for a given i18next `lng` and namespace. */
export function buildStringsUrl(
  lng: string,
  namespace: string = LEGACY_FALLBACK_NAMESPACE,
): string {
  return `${getCloudFrontBaseUrl()}${STRINGS_BASE_PATH}/${lng}/${namespace}.json`;
}

/**
 * TODO(TCM V1 deprecation): remove together with `LEGACY_STRINGS_PATH`.
 * Builds the legacy (V1) language-agnostic strings URL.
 */
export function buildLegacyStringsUrl(): string {
  return `${getCloudFrontBaseUrl()}${LEGACY_STRINGS_PATH}`;
}

/** Fetches a strings JSON, throwing on a non-OK response. */
async function fetchStrings(url: string): Promise<InterfaceAppString> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load strings from ${url}: ${response.status}`);
  }
  return response.json();
}

/**
 * Legacy V1 `strings.json` may still ship the old `APP_NAME` key. Consumers
 * read `PAM_APP_NAME`, so if a V1 payload only has `APP_NAME`, copy its value
 * into `PAM_APP_NAME` before merging. V2 `pam.json` is expected to always
 * carry `PAM_APP_NAME` directly, so no normalization runs there.
 */
function withPamAppNameFallback(
  strings: InterfaceAppString,
): InterfaceAppString {
  if (!strings?.PAM_APP_NAME && strings?.APP_NAME) {
    return { ...strings, PAM_APP_NAME: strings.APP_NAME };
  }
  return strings;
}

/**
 * Fetches one namespace's V2 file. Only the primary `pam-pam` namespace falls
 * back to the legacy V1 endpoint on failure (network error or missing file in
 * an environment that hasn't migrated yet).
 */
async function fetchNamespaceStrings(
  lng: string,
  namespace: string,
): Promise<InterfaceAppString> {
  try {
    return await fetchStrings(buildStringsUrl(lng, namespace));
  } catch (error) {
    if (namespace !== LEGACY_FALLBACK_NAMESPACE) throw error;
    // TODO(TCM V1 deprecation): remove this fallback once TCM V1 is deprecated
    // and all environments serve the per-language V2 files.
    console.warn(
      `TCM V2 strings unavailable for "${lng}", falling back to V1.`,
      error,
    );
    return withPamAppNameFallback(await fetchStrings(buildLegacyStringsUrl()));
  }
}

/**
 * Fetches the per-language namespace files from TCM V2 and merges them into
 * AppStrings in `namespaces` list order (later namespaces win). No-ops when
 * the requested language+namespaces combination is already loaded (e.g. `en`
 * from boot).
 *
 * The `pam-pam` namespace is required — if both its V2 file and the legacy V1
 * fallback fail, this rejects (as before the namespace split). Any other
 * namespace that fails to load is skipped with a warning so a missing
 * secondary bundle can't take the app down.
 */
export async function loadAppStringsForLanguage(
  lng: string,
  namespaces: readonly string[] = APP_STRING_NAMESPACES,
): Promise<InterfaceAppString | undefined> {
  const requestKey = `${lng}|${namespaces.join(",")}`;
  if (requestKey === loadedKey) return undefined;
  if (inFlight?.key === requestKey) return inFlight.promise;

  const promise = (async (): Promise<InterfaceAppString> => {
    let merged: InterfaceAppString = {};
    for (const namespace of namespaces) {
      try {
        merged = {
          ...merged,
          ...(await fetchNamespaceStrings(lng, namespace)),
        };
      } catch (error) {
        if (namespace === LEGACY_FALLBACK_NAMESPACE) throw error;
        console.warn(
          `Strings namespace "${namespace}" unavailable for "${lng}"; skipping.`,
          error,
        );
      }
    }

    setAppString(merged);
    if (merged?.PAM_APP_NAME) {
      document.title = merged.PAM_APP_NAME;
    }
    loadedKey = requestKey;
    return merged;
  })();

  inFlight = { key: requestKey, promise };
  try {
    return await promise;
  } finally {
    if (inFlight?.promise === promise) {
      inFlight = null;
    }
  }
}
